import { computed } from 'vue';
import { useTimestamp } from '@vueuse/core';
import { useApolloClient } from '@vue/apollo-composable';
import { useTimeTrackerDropdownQuery } from '@/generated/graphql';
import { parseDateTimeFromSAPI } from '@/utils/sapi';
import { useSocket } from '@/composables/socket';
import { SocketMessageType } from '@/constants/socketMessages';
import { APOLLO_PARAMS_PERSISTED_QUERY } from '@/lib/apollo';

/**
 * Shared composable that provides reactive access to the current ActiveTimer,
 * keeps the Apollo cache in sync via socket events, and computes the elapsed
 * duration with a ticking clock.
 *
 * Used by both the header TimeTrackerActiveIndicator and the inline
 * TimeEntryActions tracker pill so that:
 * - the GraphQL subscription is deduplicated by Apollo;
 * - socket-driven cache updates are registered by every consumer (no silent
 *   dependency on the header being mounted);
 * - the ticking `now` and `totalDurationSeconds` logic lives in one place.
 */
export function useActiveTimer() {
	const { client } = useApolloClient();
	const { result, refetch } = useTimeTrackerDropdownQuery({
		...APOLLO_PARAMS_PERSISTED_QUERY,
	});

	const activeTimer = computed(() => result.value?.ActiveTimer);

	useSocket({
		[SocketMessageType.TIME_TRACKER_STARTED]: () => {
			void refetch?.()?.catch(() => {});
		},
		[SocketMessageType.TIME_TRACKER_PAUSED]: () => {
			client.cache.modify({
				fields: {
					ActiveTimer: () => null,
				},
			});
		},
		[SocketMessageType.TIME_TRACKER_COMPLETED]: (message) => {
			const incomingId = String(message.data.tracker_id);
			if (incomingId === activeTimer.value?.id) {
				client.cache.modify({
					fields: {
						ActiveTimer: () => null,
					},
				});
			}
		},
		[SocketMessageType.TIME_ENTRY_UPDATED]: (message) => {
			const incomingId = String(message.data.time_entry_id);
			if (incomingId === activeTimer.value?.id) {
				void refetch?.()?.catch(() => {});
			}
		},
		[SocketMessageType.TIME_ENTRY_DELETED]: (message) => {
			const incomingId = String(message.data.time_entry_id);
			if (incomingId === activeTimer.value?.id) {
				client.cache.modify({
					fields: {
						ActiveTimer: () => null,
					},
				});
			}
		},
	});

	const dateTimeNow = useTimestamp({ interval: 1000 });

	const totalDurationSeconds = computed(() => {
		if (!activeTimer.value || !activeTimer.value.startedDateTime) {
			return 0;
		}

		const previousDuration = activeTimer.value.timerDuration || 0;
		const startedAt = parseDateTimeFromSAPI(
			activeTimer.value.startedDateTime
		);
		const currentDuration = dateTimeNow.value - startedAt.getTime();
		const totalDuration =
			previousDuration + Math.floor(currentDuration / 1000);

		return Math.max(totalDuration, 0);
	});

	return { activeTimer, refetch, dateTimeNow, totalDurationSeconds };
}
